import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSeo } from "@/hooks/usePageSeo";
import { AlertTriangle } from "lucide-react";

const DisclaimerPage = () => {
  const { language, url, onLanguageChange } = usePageSeo();

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Disclaimer - ROC Worldwide Agency LLC</title>
        <meta name="description" content="Important disclaimers and limitations regarding Luis ROC digital marketing and SEO consulting services." />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Disclaimer - Luis ROC" />
        <meta property="og:description" content="Legal disclaimers for ROC Worldwide Agency LLC services." />
        <meta property="og:url" content={url} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="section-padding">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto space-y-8">
              <div className="text-center">
                <h1 className="text-3xl lg:text-4xl font-bold mb-4 heading-gradient">
                  Disclaimer
                </h1>
                <p className="text-lg text-muted-foreground">
                  Important Information Regarding Our Services
                </p>
              </div>

              <Card className="card-elegant border-amber-200 bg-amber-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                    <h2 className="text-xl font-bold text-amber-800">Important Notice</h2>
                  </div>
                  <p className="text-amber-700">
                    The information contained on this website and our services are provided on an "as is" basis. 
                    Please read the following disclaimers carefully before using our services or relying on any information provided.
                  </p>
                </CardContent>
              </Card>

              <Card className="card-elegant">
                <CardContent className="p-8 space-y-6">
                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">No Guarantee of Results</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      ROC Worldwide Agency LLC provides professional consulting services based on industry 
                      best practices, proven methodologies, and extensive experience. However, we cannot and do not guarantee 
                      specific results from our services, including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Increased website traffic or search engine rankings</li>
                      <li>Higher conversion rates or sales figures</li>
                      <li>Improved return on investment (ROI) from marketing activities</li>
                      <li>Specific timeframes for achieving marketing objectives</li>
                      <li>Protection against algorithm changes by search engines or platforms</li>
                    </ul>
                    <p className="text-muted-foreground leading-relaxed mt-4">
                      Results depend on numerous factors including market conditions, competition, client implementation of 
                      recommendations, budget allocation, and external factors beyond our control.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Industry Changes and Algorithm Updates</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Digital marketing, SEO, and online advertising are dynamic fields subject to frequent changes including 
                      search engine algorithm updates, platform policy changes, and evolving best practices. While we stay 
                      current with industry developments and adapt our strategies accordingly, we cannot predict or control these 
                      changes. Past performance does not guarantee future results, and strategies that worked previously may need 
                      adjustment as the digital landscape evolves.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Client Responsibility</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      The success of our consulting services largely depends on client participation and implementation. Clients are responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                      <li>Providing accurate and complete information about their business</li>
                      <li>Implementing recommended strategies and tactics in a timely manner</li>
                      <li>Allocating adequate resources (time, budget, personnel) for implementation</li>
                      <li>Maintaining compliance with all applicable laws and platform policies</li>
                      <li>Monitoring and reporting on the performance of implemented strategies</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Third-Party Platforms and Services</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Our services may involve recommendations for or work with third-party platforms such as Google, Facebook, 
                      Amazon, and other digital marketing platforms. We are not affiliated with, endorsed by, or responsible for 
                      the policies, performance, or availability of these third-party services. Changes to third-party platforms, 
                      their algorithms, policies, or pricing are outside our control and may impact the effectiveness of implemented strategies.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Investment and Financial Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Digital marketing and SEO services require financial investment with no guarantee of return. Clients should 
                      carefully consider their budget and business objectives before engaging our services. We recommend viewing 
                      marketing investments as long-term strategies rather than short-term guarantees. Past client successes do not 
                      guarantee similar results for your business.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Competitive Analysis Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Competitive analysis and market research provided as part of our services are based on publicly available 
                      information and industry tools. This information may not be complete, accurate, or current. We do not have 
                      access to proprietary information about competitors' strategies, budgets, or internal operations. 
                      Competitive insights should be used as general guidance rather than definitive market intelligence.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Website and Content Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      The information on this website is for general informational purposes only and should not be considered as 
                      professional advice for your specific situation. While we strive to keep information current and accurate, 
                      we make no representations or warranties about the completeness, accuracy, reliability, suitability, or 
                      availability of the website or information contained herein.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Case Studies and Testimonials</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Case studies and testimonials presented on our website represent specific client experiences and results. 
                      These examples should not be interpreted as typical results or guarantees of what you can expect. Individual 
                      results vary based on numerous factors including business model, market conditions, budget, implementation 
                      quality, and commitment level.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Technical Limitations</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Our recommendations and implementations are subject to technical limitations of websites, hosting platforms, 
                      content management systems, and other technical infrastructure. We cannot guarantee that all recommendations 
                      can be implemented due to technical constraints, budget limitations, or platform restrictions.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Legal and Compliance Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Clients are responsible for ensuring that all marketing activities comply with applicable local, state, 
                      federal, and international laws and regulations. This includes but is not limited to advertising standards, 
                      data protection laws, accessibility requirements, and industry-specific regulations. We recommend consulting 
                      with legal counsel regarding compliance matters specific to your business and industry.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Limitation of Liability</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      To the fullest extent permitted by law, ROC Worldwide Agency LLC shall not be liable for 
                      any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
                      whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, 
                      resulting from your use of our services.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Updates to This Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      We reserve the right to modify this disclaimer at any time. Changes will be effective immediately upon 
                      posting to our website. Your continued use of our services after any such changes constitutes your 
                      acceptance of the new disclaimer.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-2xl font-bold mb-4 text-primary">Contact Information</h2>
                    <div className="text-muted-foreground leading-relaxed">
                      <p className="mb-4">
                        If you have questions about this disclaimer or our services, please contact us:
                      </p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <p className="mb-2">
                          <strong>ROC Worldwide Agency LLC</strong>
                        </p>
                        <p>9002 Six Pines Dr, Suite 277</p>
                        <p>Shenandoah, TX 77380, United States</p>
                        <p>Phone: (832) 521-5050</p>
                        <p>Email: luisroc@luisroc.com</p>
                      </div>
                    </div>
                  </section>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        
        <Footer language={language} />
      </div>
    </>
  );
};

export default DisclaimerPage;